/** Voos: companhias, aeroportos e link de check-in (usado na API e na tela). */

export const COMPANHIAS: Record<string, string> = {
  G3: 'GOL',
  LA: 'LATAM',
  JJ: 'LATAM',
  AD: 'Azul',
  '2Z': 'Voepass',
  TP: 'TAP',
  AR: 'Aerolíneas Argentinas',
  CM: 'Copa Airlines',
  AV: 'Avianca',
  AA: 'American Airlines',
  UA: 'United',
  DL: 'Delta',
  AF: 'Air France',
  KL: 'KLM',
  IB: 'Iberia',
  UX: 'Air Europa',
  LH: 'Lufthansa',
  EK: 'Emirates',
  QR: 'Qatar Airways',
  TK: 'Turkish Airlines',
  H2: 'Sky Airline',
  JA: 'JetSMART',
};

export function nomeCompanhia(iata: string | null | undefined, fallback?: string | null): string {
  if (!iata) return fallback ?? '';
  return COMPANHIAS[iata.toUpperCase()] ?? fallback ?? iata.toUpperCase();
}

/** Cidade dos aeroportos mais comuns (o resto aparece só com o código). */
export const AEROPORTOS: Record<string, string> = {
  SSA: 'Salvador',
  FEC: 'Feira de Santana',
  IOS: 'Ilhéus',
  BPS: 'Porto Seguro',
  VDC: 'Vitória da Conquista',
  LEC: 'Lençóis',
  GRU: 'São Paulo (Guarulhos)',
  CGH: 'São Paulo (Congonhas)',
  VCP: 'Campinas (Viracopos)',
  GIG: 'Rio de Janeiro (Galeão)',
  SDU: 'Rio de Janeiro (Santos Dumont)',
  BSB: 'Brasília',
  CNF: 'Belo Horizonte (Confins)',
  PLU: 'Belo Horizonte (Pampulha)',
  REC: 'Recife',
  FOR: 'Fortaleza',
  NAT: 'Natal',
  JPA: 'João Pessoa',
  MCZ: 'Maceió',
  AJU: 'Aracaju',
  THE: 'Teresina',
  SLZ: 'São Luís',
  BEL: 'Belém',
  MAO: 'Manaus',
  POA: 'Porto Alegre',
  CWB: 'Curitiba',
  FLN: 'Florianópolis',
  NVT: 'Navegantes',
  IGU: 'Foz do Iguaçu',
  VIX: 'Vitória',
  GYN: 'Goiânia',
  CGB: 'Cuiabá',
  CGR: 'Campo Grande',
  PMW: 'Palmas',
  FEN: 'Fernando de Noronha',
  JDO: 'Juazeiro do Norte',
  PNZ: 'Petrolina',
  MOC: 'Montes Claros',
  UDI: 'Uberlândia',
  RAO: 'Ribeirão Preto',
  LIS: 'Lisboa',
  OPO: 'Porto',
  MAD: 'Madri',
  BCN: 'Barcelona',
  CDG: 'Paris',
  ORY: 'Paris (Orly)',
  FCO: 'Roma',
  MXP: 'Milão',
  LHR: 'Londres',
  AMS: 'Amsterdã',
  FRA: 'Frankfurt',
  MIA: 'Miami',
  MCO: 'Orlando',
  JFK: 'Nova York (JFK)',
  EWR: 'Nova York (Newark)',
  FLL: 'Fort Lauderdale',
  EZE: 'Buenos Aires (Ezeiza)',
  AEP: 'Buenos Aires (Aeroparque)',
  SCL: 'Santiago',
  MVD: 'Montevidéu',
  LIM: 'Lima',
  BOG: 'Bogotá',
  PTY: 'Cidade do Panamá',
  CUN: 'Cancún',
  PUJ: 'Punta Cana',
  DXB: 'Dubai',
  DOH: 'Doha',
  IST: 'Istambul',
};

export function nomeAeroporto(iata: string): string {
  return AEROPORTOS[iata.toUpperCase()] ?? iata.toUpperCase();
}

/** Página de check-in da companhia (GOL já abre com o localizador preenchido). */
export function checkinUrl(companhia: string, localizador: string, origem: string, extra?: { pedido?: string | null; sobrenome?: string | null }): string | null {
  const cia = companhia.toUpperCase();
  if (cia === 'G3') return `https://b2c.voegol.com.br/check-in/?recordLocator=${encodeURIComponent(localizador)}&departureAirport=${encodeURIComponent(origem)}`;
  if (cia === 'LA' || cia === 'JJ') return latamUrl(extra) ?? 'https://www.latamairlines.com/br/pt/checkin';
  if (cia === 'AD') return azulUrl(localizador, origem, 'checkin');
  return null;
}

/** "Minhas viagens" da GOL direto na reserva; sem sobrenome ou origem, abre a busca. */
function golReservaUrl(localizador: string, origem: string, sobrenome?: string | null): string {
  const busca = 'https://b2c.voegol.com.br/minhas-viagens/encontrar-viagem';
  if (!sobrenomeValido(sobrenome) || !/^[A-Za-z]{3}$/.test(origem)) return busca;
  return `${busca}?codigoReserva=${encodeURIComponent(localizador)}&origin=${encodeURIComponent(origem.toUpperCase())}&sobrenome=${encodeURIComponent(sobrenome.trim())}`;
}

/** "Minhas viagens" da LATAM direto na reserva (número do pedido + sobrenome). */
function latamReservaUrl(extra?: { pedido?: string | null; sobrenome?: string | null }): string | null {
  if (!extra?.pedido || !sobrenomeValido(extra.sobrenome)) return null;
  return `https://www.latamairlines.com/br/pt/minhas-viagens/second-detail?orderId=${encodeURIComponent(extra.pedido)}&lastname=${encodeURIComponent(extra.sobrenome)}`;
}

/** Azul: check-in e "Minhas viagens" abrem já na reserva (localizador + aeroporto de saída). */
function azulUrl(localizador: string, origem: string, pagina: 'checkin' | 'viagem'): string {
  const caminho = pagina === 'checkin' ? 'azulwebcheckin' : 'minhas-viagens';
  return `https://www.voeazul.com.br/br/pt/home/${caminho}?pnr=${encodeURIComponent(localizador)}&origin=${encodeURIComponent(origem)}`;
}

/** Check-in da LATAM direto na reserva com o número do pedido ("LA...") e o sobrenome. */
function latamUrl(extra?: { pedido?: string | null; sobrenome?: string | null }): string | null {
  if (!extra?.pedido || !sobrenomeValido(extra.sobrenome)) return null;
  return `https://www.latamairlines.com/br/pt/checkin/status?orderId=${encodeURIComponent(extra.pedido)}&lastName=${encodeURIComponent(extra.sobrenome)}`;
}

/**
 * Horários dos voos são guardados como "hora local do aeroporto" (sem fuso).
 * Na API e na tela eles circulam como texto "AAAA-MM-DDTHH:MM".
 */
export function formatHoraLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(11, 16);
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** "seg, 15 dez" ou, com `longo`, "segunda, 15 de dez de 2026". */
export function formatDataLocal(iso: string | null | undefined, longo = false): string {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const dia = DIAS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()] ?? '';
  return longo ? `${dia}, ${d} de ${MESES[m - 1]} de ${a}` : `${dia.slice(0, 3)}, ${String(d).padStart(2, '0')} ${MESES[m - 1]}`;
}

/**
 * Página de GERENCIAR RESERVA da companhia (não a de check-in), para conferir a reserva.
 * Abrem direto na reserva: GOL (localizador + origem + sobrenome) e LATAM (pedido + sobrenome).
 * A GOL aceita a origem de qualquer trecho da reserva (testado com a da ida e a da volta).
 */
export function consultaReservaUrl(companhia: string, localizador: string, origem: string, extra?: { pedido?: string | null; sobrenome?: string | null }): string | null {
  const cia = companhia.toUpperCase();
  if (cia === 'G3') return golReservaUrl(localizador, origem, extra?.sobrenome);
  if (cia === 'LA' || cia === 'JJ') return latamReservaUrl(extra) ?? 'https://www.latamairlines.com/br/pt/minhas-viagens';
  if (cia === 'AD') return azulUrl(localizador, origem, 'viagem');
  return null;
}

export const CONFERENCIA_STATUS_LABELS = {
  OK: 'Reserva OK',
  CANCELADA: 'Cancelada',
  ALTERADA: 'Alterada',
  NAO_ENCONTRADA: 'Não encontrada',
} as const;
export type ConferenciaStatusKey = keyof typeof CONFERENCIA_STATUS_LABELS;

/** A companhia abre direto na reserva por link (sem colar nada)? GOL com sobrenome; LATAM com pedido + sobrenome. */
export function abreDireto(companhia: string, pedido?: string | null, sobrenome?: string | null): boolean {
  const cia = companhia.toUpperCase();
  if (cia === 'G3') return sobrenomeValido(sobrenome);
  if (cia === 'AD') return true;
  return (cia === 'LA' || cia === 'JJ') && Boolean(pedido) && sobrenomeValido(sobrenome);
}

/** Sobrenome utilizável no site da companhia (bilhetes às vezes vêm mascarados: "S****"). */
export function sobrenomeValido(s: string | null | undefined): s is string {
  return Boolean(s && s.trim().length >= 2 && !s.includes('*'));
}
